'use client';
import { Stack } from '@mui/material';
import { provinces } from '@repo/onb-api';
import { Dropdown } from '@repo/ui';
import { formatter, getCityData, getCityDataById, getIdByCityData } from '@repo/utils';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

type ProvinceDropdownsProps = {
  id?: number;
  defaultProvince?: string;
  onDropdownChange?: () => void;
  onCompleted: (_result: number | undefined) => void;
  onBlur?: Function;
};

export type ProvinceDropdownsRef = {
  validate: () => boolean;
};

const formatOptions = (options: string[]) =>
  options
    .map((option) => ({
      label: formatter(option, 'capitalize'),
      value: option,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Renders a ProvinceDropdowns component with 3 required dropdowns with province, partido and locality selection.
 *
 * @param {number} id - The optional localityId to load initial data on each dropdown.
 * @param {string} defaultProvince - The optional string to preload the province dropdown.
 * @param {Function} onDropdownChange - The optional function to run with every dropdown change.
 * @param {Function} onCompleted - The function that runs when all dropdowns are completed which returns a localityId number.
 * @param {Function} onBlur - The function exposed to the form parent to trigger internal validation.
 * @return {JSX.Element} The rendered ProvinceDropdowns component.
 */
export const ProvinceDropdowns = forwardRef<ProvinceDropdownsRef, ProvinceDropdownsProps>(
  ({ id, defaultProvince, onDropdownChange, onCompleted }, ref) => {
    const [province, setProvince] = useState(defaultProvince ?? '');
    const [provinceError, setProvinceError] = useState(false);
    const [partido, setPartido] = useState('');
    const [partidoOptions, setPartidoOptions] = useState<{ label: string; value: string }[]>([]);
    const [partidoError, setPartidoError] = useState(false);
    const [locality, setLocality] = useState('');
    const [localityOptions, setLocalityOptions] = useState<{ label: string; value: string }[]>([]);
    const [localityError, setLocalityError] = useState(false);
    const partidoDisabled = !province || partidoOptions?.length === 0;
    const localityDisabled = !partido || localityOptions?.length === 0;

    useEffect(() => {
      const loadInitialData = async () => {
        try {
          if (id) {
            const cityData = await getCityDataById(id);
            if (!cityData) return;
            const { nombreProvincia, nombrePartido, nombreLocalidad } = cityData;
            setProvince(nombreProvincia);
            const partidos = await getCityData({
              filters: { nombreProvincia },
              targetKey: 'nombrePartido',
            });
            setPartidoOptions(formatOptions(partidos));
            setPartido(nombrePartido);
            const localities = await getCityData({
              filters: { nombreProvincia, nombrePartido },
              targetKey: 'nombreLocalidad',
            });
            setLocalityOptions(formatOptions(localities));
            setLocality(nombreLocalidad);
          } else if (!!defaultProvince && partidoOptions.length === 0) {
            const partidos = await getCityData({
              filters: { nombreProvincia: defaultProvince.replaceAll('_', ' ') },
              targetKey: 'nombrePartido',
            });
            setPartidoOptions(formatOptions(partidos));
          }
        } catch (error) {
          console.error('Failed to load initial city data:', error);
        }
      };
      loadInitialData();
    }, []);

    useImperativeHandle(ref, () => ({
      validate: () => {
        setProvinceError(!province);
        setPartidoError(partidoDisabled ? false : !partido);
        setLocalityError(localityDisabled ? false : !locality);
        return !(!province || !partido || !locality);
      },
    }));

    return (
      <Stack gap={2}>
        <Dropdown
          label="Provincia"
          placeholder="Ej: Buenos Aires"
          value={province.toUpperCase().replaceAll('_', ' ')}
          data-testid="province"
          required
          error={provinceError}
          helperText={provinceError ? 'Seleccioná una provincia' : ''}
          options={provinces.map((p) => ({
            label: formatter(p, 'capitalize') as string,
            value: p.replaceAll('_', ' '),
          }))}
          onBlur={() => setProvinceError(!province)}
          onChange={(event) => {
            const value = (event.target as HTMLSelectElement)?.value;
            setProvince(value);
            setProvinceError(false);
            setPartido('');
            getCityData({
              filters: { nombreProvincia: value.replaceAll('_', ' ') },
              targetKey: 'nombrePartido',
            })
              .then((result: string[]) => {
                setPartidoOptions(formatOptions(result));
              })
              .catch((error) => console.error(error));
            setLocality('');
            setLocalityOptions([]);
            onDropdownChange?.();
          }}
        />
        <Dropdown
          label="Partido"
          placeholder="Ej: Tigre"
          value={partido}
          data-testid="partido"
          required
          error={partidoError}
          helperText={partidoError ? 'Seleccioná un partido' : ''}
          disabled={partidoDisabled}
          options={partidoOptions ?? []}
          onBlur={() => setPartidoError(!!(province && !partido))}
          onChange={(event) => {
            setPartidoError(false);
            setPartido((event.target as HTMLSelectElement)?.value);
            setLocality('');
            setLocalityError(!!(province && partido && !locality));
            getCityData({
              filters: {
                nombrePartido: (event.target as HTMLSelectElement)?.value.replaceAll('_', ' '),
                nombreProvincia: province?.replaceAll('_', ' '),
              },
              targetKey: 'nombreLocalidad',
            })
              .then((value: string[]) => {
                setLocalityOptions(formatOptions(value));
              })
              .catch((error) => console.error(error));
            onDropdownChange?.();
          }}
        />
        <Dropdown
          label="Localidad"
          placeholder="Ej: Don Torcuato"
          value={locality}
          data-testid="locality"
          required
          error={localityError}
          helperText={localityError ? 'Seleccioná una localidad' : ''}
          disabled={localityDisabled}
          options={localityOptions ?? []}
          onBlur={() => setLocalityError(!!(province && partido && !locality))}
          onChange={(event) => {
            const selectedLocality = (event.target as HTMLSelectElement)?.value;
            setLocality(selectedLocality);
            setLocalityError(false);
            if (province && partido) {
              getIdByCityData({
                nombreProvincia: province.replaceAll('_', ' '),
                nombrePartido: partido,
                nombreLocalidad: selectedLocality,
              })
                .then((value) => {
                  onCompleted?.(value);
                })
                .catch((error) => console.error('Failed to get localityId with cityData', error));
            }
          }}
        />
      </Stack>
    );
  },
);
